import { isJSONataConditionalQuery } from './utils';
import { Data, ConditionalFn, DigOptions } from './model';

// jsonata should be provided as external dependency as a global variable
// and imports the min file for browsers (for node js it's not required to
// do so since it is still packed in node module exported by webpack)
declare var jsonata: any;

/**
 * Generic type that allows another type to accept null / undefined
 * to bypass nullable params
 */
type Nullable<T> = T | null | undefined;

/**
 * Recursive method which retrieves the value from a nested
 * object
 *
 * @param data The record (only objects NO arrays).
 * @param path Array of strings representing a deep object keys
 * @param idx Current iteration index
 * @returns Value or object found using the path.
 */
const dataExtractor = (data: { [key: string]: any }, path: string[], idx = 0): any => {
  const propName = path[idx];
  if (idx === path.length - 1 || data == null) {
    return propName.length && data ? data[propName] : data;
  }
  const next = data[propName];
  return dataExtractor(next, path, idx + 1);
};

/**
 * Recursive method which retrieves the value from a nested
 * object using the provided path. The return may be a single value
 * or an array of objects.
 *
 * @param data or object that might have deep nested arrays due recursion
 * sample input: [[[1,2,3]]] | [[1], [2], [3]] | [[1], [2], 3]
 * @returns array of objects/values.
 * sample output: [1, 2, 3]
 */
const unpack = (data: { [key: string]: any }): any =>
  Array.isArray(data)
    ? data.reduce((acc, subset) => [...acc, ...(Array.isArray(subset) ? subset : [subset])], [])
    : data;

/**
 * Recursive method which retrieves the value from a nested
 * object using the provided path. The return may be a single value
 * or an array of objects.
 *
 * @param data Object to get data from
 * @param path Array of arrays that contains the path split by array (/).
 * @param idx Current index.
 * @returns Value or array of objects found using the path.
 */
export const deepDigger = (data: { [key: string]: any }, path: string[][], idx = 0): any => {
  if (idx === path.length - 1) {
    return dataExtractor(data, path[idx]);
  }
  // move deep and restart digging
  data = path[idx].reduce((acc, key) => (acc != null && acc[key] != null ? acc[key] : null), data);
  data = Array.isArray(data) ? data : [data];
  return !!data ? unpack(data.map((subset: any) => deepDigger(subset, path, idx + 1))) : null;
};

/**
 * Uses third party library (JSONata) to query over passed data
 * @see {@link https://jsonata.org/}
 * @param data {Nullable<Data>}
 * @param path {string}
 * @returns any
 */
export const jsonataDig = (data: Nullable<Data>, path: string): any => {
  const expression = jsonata(path);
  const result = expression.evaluate(data);
  return result;
};

/**
 * Retrieves the value from a nested object using the provided path string. The
 * return may be a single value or an array of objects.
 *
 * If `options` is provided and is a conditionalFn type (function) or a `DigOptions` object with condition
 * function (`conditionalFn` type) provided it will filter all the values that do not match the given
 * condition.
 *
 * If `DigOptions` is provided and useJsonata is enabled (true) digger will proxy
 * the extraction to `jsonataDig` helper which internally uses `jsonata` library
 * query through the data
 *
 * If `pathStr` also matches with **JSONAta boolean operators** @see {@link https://docs.jsonata.org/boolean-operators}
 * (in short includes '[' and ']') then by default uses jsonata since digger
 * don't support this type of pathStr to resolve a value
 *
 * @param data Object to get data from
 * @param pathStr Path representing levels of nested arrays formatted like
 *   'property.to.access/array_prop'
 * @param options Function | DigOptions used to filter returned items based on given criteria in the
 * function
 * @returns Value or array of objects found using the path.
 */
export function digger(data: Nullable<Data>, pathStr: string, options?: ConditionalFn | DigOptions): any {
  options = options == null ? {} : options;
  if (typeof options === 'function') {
    options = { condition: options, useJsonata: false };
  }

  if (!data || Object.keys(data).length === 0 || !pathStr) {
    return '';
  } else if (!!options.condition && !options.useJsonata && !pathStr.includes('/')) {
    throw new Error("Can't execute conditional digger due to path not belonging to an array");
  }

  if (options.useJsonata || isJSONataConditionalQuery(pathStr)) {
    const res = jsonataDig(data, pathStr);
    return res;
  }

  const fullSplitPath = pathStr.split('/');
  let arrayValuePath: string[][] = [];
  if (fullSplitPath.length > 1) {
    arrayValuePath = [fullSplitPath.splice(fullSplitPath.length - 1, 1)];
    arrayValuePath = arrayValuePath.map(folders =>
      folders.reduce((acc, folder) => [...acc, ...folder.split('.')], [] as string[])
    );
  }
  const path = fullSplitPath.map(folders => folders.split('.'));
  let diggerResult = deepDigger(data, path);

  if (Array.isArray(diggerResult) && !!arrayValuePath.length) {
    const { condition } = options;
    if (!!condition && typeof condition === 'function') {
      diggerResult = diggerResult.filter(obj => condition(obj));
    }
    diggerResult = unpack(diggerResult.map((subset: any) => deepDigger(subset, arrayValuePath)));
  }
  return diggerResult;
}
